import { IBlockConfig } from '@core/models/marketplace.model';

import { resolveLinkValuesInTree, type ShopRef } from '@front-platform/content-builder';

import { resolveParamRefTarget } from './content-builder-param-ref.util';
import { transformResolvedPropTree } from './content-builder-prop-format.util';
import { resolveBoundValue, RuntimeContextSnapshot } from './resolve-bound-value';

export interface ResolvePreviewBlocksOptions {
  context: RuntimeContextSnapshot;
  /** Catalog param values pushed by the studio (opaque, may contain $param / $cases). */
  paramValues?: Record<string, unknown>;
  /** Param types, used to resolve palette paths (ui.colors.neutral-900 → hex). */
  paramCatalog?: Record<string, { type?: string }>;
  /** Resolves the active shop for `activeShop` link bindings (preview store or stub). */
  resolveActiveShop?: () => ShopRef | null;
}

/**
 * Resolves each block's `data` for the current preview context:
 * literal / $param / $cases resolution, then dood-front prop formatting (colors, images).
 *
 * Pure and side-effect free: returns a new block list, leaving the input untouched so
 * the caller can dispatch the result into the content store.
 */
export function resolvePreviewBlocks(
  blocks: IBlockConfig[],
  options: ResolvePreviewBlocksOptions,
): IBlockConfig[] {
  const { context } = options;
  const paramValues = options.paramValues ?? {};
  const paramCatalog = options.paramCatalog ?? {};
  const resolveParamRef = (refKey: string) =>
    resolveParamRefTarget(paramValues, refKey, paramCatalog);

  return blocks.map(block => {
    if (!block?.data || typeof block.data !== 'object') {
      return block;
    }
    const resolved = resolveBoundValue(block.data, {
      context,
      lang: context.locale,
      resolveParamRef,
    });
    const withLinks = resolveLinkValuesInTree(resolved ?? block.data, {
      resolveParam: resolveParamRef,
      resolveActiveShop: options.resolveActiveShop,
    });
    const formatted = transformResolvedPropTree(withLinks);
    return { ...block, data: formatted };
  });
}
